import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgForm } from '@angular/forms';
import moment from 'moment';
import flatpickr from 'flatpickr';
import { LocalizationService } from 'src/app/services/localization.service';
import { BaseSimpleDialogComponent } from '../base-simple-dialog/base-simple-dialog.component';
import { LoggerService } from '../../../services/logger.service';

/**
 * Enrollment Drop Component
 *
 * REQUIREMENTS AND BEHAVIOR:
 *
 * 1. General Purpose:
 *    - Allows administrators to drop an enrollment and optionally process a refund
 *    - Calculates refund amounts based on lessons taken and payment structure
 *
 * 2. Enrollment Categories:
 *    - Standard enrollments (Original, Pre-Original, Renewals, etc.): Drop based on lessons taken/remaining
 *    - Miscellaneous enrollments (category tid 5): Always droppable, full amount refundable
 *    - Sundry enrollments (category tid 67): Should be handled like miscellaneous enrollments
 *    - Non-Unit enrollments (category tid 66): Special handling for non-lesson-based enrollments
 *    - Bonus/Comp Lesson, Transfer In, etc.: Each may have specific drop rules
 *
 * 3. Drop Logic:
 *    - Standard enrollments: Can't be dropped if all lessons are used
 *    - Miscellaneous/Sundry enrollments: Can always be dropped regardless of lessons
 *    - The system prevents dropping if field_dropped_date has a value (already dropped)
 *
 * 4. Refund Calculation:
 *    - Standard enrollments: (Total paid - Value of lessons taken - Taxes)
 *    - Miscellaneous/Sundry enrollments: Full amount refundable
 *    - Value of lessons taken = lessons taken * (total price / total lessons)
 *
 * 5. Adjustment Row Generation:
 *    - After dropping, an adjustment row appears in student reports
 *    - For standard enrollments: Shows lessons taken/paid and unused lessons dropped
 *    - For miscellaneous/sundry enrollments: Shows full amount dropped
 *
 * 6. Backend Integration:
 *    - Uses /api_rest/v1/dropEnrollment endpoint to process the drop
 *    - Uses /api_rest/v1/enrollmentTotalPriceTotalDropAmounts to get drop amounts
 *    - Sets field_drop value in database (full amount for miscellaneous/sundry)
 *    - Sets field_dropped_date with the selected drop date
 *
 * NOTE: The drop process for special enrollments (Miscellaneous tid 5, Sundry tid 67) requires
 * special handling since they often have 0 lessons but still need to be droppable and refundable.
 */
@Component({
  selector: 'app-enrollment-drop',
  templateUrl: './enrollment-drop.component.html',
  styleUrls: ['./enrollment-drop.component.css']
})
export class EnrollmentDropComponent extends BaseSimpleDialogComponent implements AfterViewInit, OnDestroy {

  @ViewChild('dropDateInput') dropDateInput!: ElementRef;
  droppedData;
  // Live server-computed refund state (real payments, never the stored
  // rollup or a paid-in-full assumption). Null until loaded.
  refundableData;
  cancellationFee: number | null = null;
  // How a fee collected without a refund was paid (defaults to Cash).
  feePaymentType = '71';
  now = moment();
  dropDateDisplayValue: string = '';
  dropDateValue: Date | null = null;
  private dropDatePicker?: flatpickr.Instance;
  private localizationService = new LocalizationService();

  // Helper method to make parseFloat available in the template
  parseFloat(value: any): number {
    return parseFloat(value) || 0;
  }

  // Helper method to format currency values
  formatCurrency(value: any): string {
    return this._utilityService.formatCurrency(value);
  }

  // Helper to calculate the value of lessons taken
  calculateLessonsTakenValue(): number {
    if (!this.enrollmentEntityData) return 0;

    const lessonsTaken = parseFloat(this.enrollmentEntityData.field_lesson_used) || 0;
    const totalLessons = parseFloat(this.enrollmentEntityData.field_enrollment_lesson_count) || 0;
    const totalPrice = parseFloat(this.enrollmentEntityData.field_enrollment_total_price) || 0;

    // Calculate the actual per-lesson price based on total enrollment price divided by lesson count
    // This ensures discounts are properly accounted for
    const actualLessonPrice = totalLessons > 0 ? totalPrice / totalLessons : 0;

    return lessonsTaken * actualLessonPrice;
  }

  // Amount actually paid, from the server's live recompute. The
  // 'paid_in_full' structure describes how they agreed to pay, not proof
  // money arrived, so it must never substitute the enrollment price.
  getTotalAmountPaid(): number {
    return this.refundableData?.['net_paid'] ?? 0;
  }

  // Helper to get the actual per-lesson price accounting for discounts
  getActualLessonPrice(): number {
    if (!this.enrollmentEntityData) return 0;

    const totalLessons = parseFloat(this.enrollmentEntityData.field_enrollment_lesson_count) || 0;
    const totalPrice = parseFloat(this.enrollmentEntityData.field_enrollment_total_price) || 0;

    // Calculate the actual per-lesson price based on total enrollment price divided by lesson count
    return totalLessons > 0 ? totalPrice / totalLessons : 0;
  }

  ngAfterViewInit(): void {
    this.initDropDatePicker();
  }

  // Studio config is app-lifetime; released so the closed dialog can be collected.
  private studioConfigSubscription: Subscription;

  ngOnDestroy(): void {
    this.dropDatePicker?.destroy();
    this.studioConfigSubscription?.unsubscribe();
  }

  private initDropDatePicker(): void {
    if (!this.dropDateInput?.nativeElement) return;

    this.studioConfigSubscription = this._studioConfigService.getStudioConfig().subscribe(config => {
      const countryCode = config?.['field_studio_address']?.[0]?.country_code || 'US';
      const formats = this.localizationService.getDateFormatsForCountry(countryCode);

      const today = new Date();
      this.dropDatePicker = flatpickr(this.dropDateInput.nativeElement, {
        dateFormat: formats.flatpickrDateFormat,
        disableMobile: true,
        defaultDate: today,
        onChange: (selectedDates) => {
          if (selectedDates.length > 0) {
            this.dropDateValue = selectedDates[0];
            this.dropDateDisplayValue = this.dropDatePicker?.input?.value || '';
          }
        }
      });

      // Set default to today
      this.dropDateValue = today;
      this.dropDateDisplayValue = this.dropDatePicker?.input?.value || '';
    });
  }

  openDropDatePicker(): void {
    this.dropDatePicker?.open();
  }

  override ngAfterContentInit(): void {
    LoggerService.log('this._injectedDialogData["data"]', this._injectedDialogData['data']);

    // Load the enrollment. TODO: Maybe pass the enrollment in. I'm not sure if the view loading has all the data.
    this._entityRESTService.getEntity('packages', 'enrollment', this._injectedDialogData['data']['EntityID'])
    .subscribe(data => {
      LoggerService.log('enrollmentEntityData', data);
      this.enrollmentEntityData = data;
      this.getDroppedData();
    })

    // Refundable state comes from the server: actual payments received (a
    // Failed terminal tap does not count), minus lessons taken and taxes,
    // capped by what the standing payments can still return.
    this._drupalRESTService.httpGET('/api_rest/v1/enrollmentRefundableAmounts', [
      { parameter: 'enrollment_id', value: this._injectedDialogData['data']['EntityID'] },
    ]).subscribe(data => {
      this.refundableData = data;
    })
  }

  // Display helpers backed by the server calculation.
  getLessonsTakenValueLive(): number {
    return this.refundableData?.['lessons_taken_value'] ?? 0;
  }

  getTaxesLive(): number {
    return this.refundableData?.['taxes'] ?? 0;
  }

  getRefundable(): number {
    return this.refundableData?.['refundable'] ?? 0;
  }

  getCancellationFee(): number {
    return Math.max(Number(this.cancellationFee) || 0, 0);
  }

  getNetRefund(): number {
    return Math.max(0, +(this.getRefundable() - this.getCancellationFee()).toFixed(2));
  }

  // The part of the fee no refund covers, collected from the student and
  // recorded as a $0-tuition fee payment, so it never enters tuition totals.
  getFeeDue(): number {
    return Math.max(0, +(this.getCancellationFee() - this.getRefundable()).toFixed(2));
  }

  getDroppedData() {
    let body = [
      { parameter: 'enrollment_id', value: this._injectedDialogData['data']['EntityID'] },
    ];
    this._drupalRESTService.httpGET('/api_rest/v1/enrollmentTotalPriceTotalDropAmounts', body).subscribe(data => {
      this.droppedData = data;
      // Special handling for miscellaneous (5) and sundry (67) enrollments
      if (this.enrollmentEntityData && (this.enrollmentEntityData.field_category == '5' || this.enrollmentEntityData.field_category == '67')) {
        this.droppedData.allLessonsTakenAndPaid = false;
      }
      LoggerService.log('droppedData', data);
    })
  }

  override onSubmit(form: NgForm, refund = false) {
    this.displayProgressSpinner(true);
    this.errorMessage = "";

    // One server-side operation: reverse the actual payments (PayBright and
    // Stripe at the gateway, everything else recorded) net of the cancellation
    // fee, and only drop once every reversal succeeded. Replaces the old flow
    // that posted a bare negative Cash payment entity and dropped regardless.
    let body = {
      enrollment_id: this._injectedDialogData['data']['EntityID'],
      drop_date: moment(this.dropDateValue).format("YYYY/MM/DD"),
      refund_amount: refund ? this.getRefundable() : 0,
      cancellation_fee: refund ? this.getCancellationFee() : 0,
      fee_payment_type: this.feePaymentType,
    };

    this._drupalRESTService.httpPOST('/api_rest/v1/dropEnrollmentWithRefund', body)
    .subscribe(data => {
      this.displayProgressSpinner(false);
      if (data['success']) {
        this.closeDialog();
      } else {
        this.errorMessage = data['message'] || "There was a problem dropping this enrollment.";
      }
    },
      error => {
        this.errorMessage = "There was a problem dropping this enrollment.";
        this.handleError(error)
        this.displayProgressSpinner(false);
      }
    );
  }

}
